import type { Option, SharedAsyncSelectProps } from "./internal/types";
import useTranslation from "@hooks/use_translation";
import { SelectWrapper } from "./internal/select_wrapper";
import { useSharedReactSelectProps } from "./internal/hooks/use_shared_react_select_props";
import { useAsyncReactSelectProps } from "./internal/hooks/use_async_react_select_props";
import { useReactSelect } from "./internal/hooks/use_react_select";

interface AsyncMultiSelectProps extends SharedAsyncSelectProps {
  onChange: (values: Option[]) => void;
  values: Option[];
}

export const AsyncMultiSelect = ({
  clearable = false,
  creatable = false,
  disabled = false,
  error,
  helperText,
  label,
  loadOptions,
  onChange,
  placeholder,
  required = false,
  values,
  ...dataProps
}: AsyncMultiSelectProps) => {
  const { t } = useTranslation("sprout");
  const { id, ...sharedAsyncReactSelectProps } = useSharedReactSelectProps(
    (newValue) => onChange(newValue as Option[]),
    { error, label },
  );
  const asyncReactSelectProps = useAsyncReactSelectProps(loadOptions);
  const ReactSelect = useReactSelect({ creatable });

  return (
    <SelectWrapper
      selectId={id}
      label={label}
      error={error}
      helperText={helperText}
      disabled={disabled}
      {...dataProps}
      required={required}
    >
      <ReactSelect
        id={id}
        {...sharedAsyncReactSelectProps}
        isClearable={clearable}
        isDisabled={disabled}
        isMulti
        placeholder={placeholder || t("select.placeholder")}
        value={values}
        {...asyncReactSelectProps}
      />
    </SelectWrapper>
  );
};
